import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// Idle timeout: 30 minutes (in milliseconds)
const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
// Token refresh interval: 50 minutes (before 60-minute expiry)
const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000; // 50 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const idleTimerRef = useRef(null);
  const tokenRefreshTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset idle timer
  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    
    // Set new idle timer
    if (user) {
      idleTimerRef.current = setTimeout(() => {
        console.log('Session expired due to inactivity');
        logout();
        alert('Your session has expired due to inactivity. Please login again.');
      }, IDLE_TIMEOUT);
    }
  }, [user]);

  // Refresh token periodically when active
  const refreshToken = useCallback(async () => {
    try {
      // Check if user is still active (activity within last 5 minutes)
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity < 5 * 60 * 1000) { // 5 minutes
        console.log('Refreshing token...');
        const response = await authAPI.getCurrentUser();
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        console.log('Token refreshed successfully');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Don't logout here - let the idle timer handle it
    }
  }, []);

  // Setup token refresh interval
  const setupTokenRefresh = useCallback(() => {
    // Clear existing interval
    if (tokenRefreshTimerRef.current) {
      clearInterval(tokenRefreshTimerRef.current);
    }
    
    // Setup new interval
    if (user) {
      tokenRefreshTimerRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
    }
  }, [user, refreshToken]);

  // Track user activity
  useEffect(() => {
    if (!user) return;

    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle activity tracking (max once per 30 seconds)
    let lastReset = 0;
    const throttledResetTimer = () => {
      const now = Date.now();
      if (now - lastReset > 30000) { // 30 seconds
        lastReset = now;
        resetIdleTimer();
      }
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, throttledResetTimer);
    });

    // Initialize timers
    resetIdleTimer();
    setupTokenRefresh();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledResetTimer);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      if (tokenRefreshTimerRef.current) {
        clearInterval(tokenRefreshTimerRef.current);
      }
    };
  }, [user, resetIdleTimer, setupTokenRefresh]);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      authAPI.getCurrentUser()
        .then(response => {
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password);
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      
      // Get user info
      const userResponse = await authAPI.getCurrentUser();
      const userData = userResponse.data;
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Reset activity tracking
      lastActivityRef.current = Date.now();
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    // Clear timers
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (tokenRefreshTimerRef.current) {
      clearInterval(tokenRefreshTimerRef.current);
    }
    
    // Clear storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAdmin = () => {
    return !!user?.is_system_role;
  };

  // Admin bypasses every check - matches the backend's require_permission
  // exactly, so the UI never shows/hides something the API would disagree with.
  const hasPermission = (key) => {
    if (!user) return false;
    return user.is_system_role || (user.permissions || []).includes(key);
  };

  const hasAnyPermission = (prefix) => {
    if (!user) return false;
    if (user.is_system_role) return true;
    return (user.permissions || []).some((p) => p.startsWith(prefix));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAdmin, hasPermission, hasAnyPermission, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
